import cn from 'classnames';
import { useRef, type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import {
    trackPromoClick,
    trackPromoImpressions,
    buildPromoTrackingObject,
} from '../helpers/promoTrackingHelpers';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';

import { Link } from 'dibs-elements/exports/Link';
import { Button } from 'dibs-elements/exports/Button';

import dibsCss from 'dibs-css';
import styles from './HpSharedFeaturedBannerModule.module.css';
import { VisibilityTracker } from 'dibs-visibility-tracker/src/VisibilityTracker';

import { type HpSharedFeaturedBannerModule_componentModule$key } from './__generated__/HpSharedFeaturedBannerModule_componentModule.graphql';

const componentModuleFragment = graphql`
    fragment HpSharedFeaturedBannerModule_componentModule on BottomBannerModule {
        title
        subText
        cta
        ctaLink
        image
        mobileImage
    }
`;

type Props = {
    isMobile: boolean;
    componentModule: HpSharedFeaturedBannerModule_componentModule$key;
};

export const HpSharedFeaturedBannerModule: FC<Props> = ({
    isMobile,
    componentModule: componentModuleRef,
}) => {
    const featuredBannerRespRef = useRef(null);
    const featuredBannerMobileRef = useRef(null);

    const componentModule = useFragment(componentModuleFragment, componentModuleRef);

    const { title, subText, cta, ctaLink, image, mobileImage } = componentModule || {};

    if (!title || !ctaLink || !image) {
        return null;
    }

    const promoTrackingObject = buildPromoTrackingObject({
        index: 0,
        id: 'bottom-banner',
        name: 'Bottom Banner',
        item: { linkData: { path: ctaLink } },
    });

    const bannerImage = isMobile && mobileImage ? mobileImage : image;

    return isMobile ? (
        <>
            <VisibilityTracker
                elementRef={featuredBannerMobileRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackPromoImpressions([promoTrackingObject]);
                    }
                }}
            />
            <Link
                href={ctaLink}
                className={styles.link}
                onClick={() => trackPromoClick(promoTrackingObject)}
            >
                <div
                    className={cn(
                        dibsCss.flex,
                        dibsCss.wFull,
                        dibsCss.flexCol,
                        dibsCss.mbLarge,
                        dibsCss.mtMedium,
                        styles.consumerBg
                    )}
                    ref={featuredBannerMobileRef}
                >
                    <div>
                        <img
                            alt=""
                            sizes="100vw"
                            src={bannerImage}
                            className={cn(styles.mobileImage, dibsCss.wFull)}
                            srcSet={getSrcsetString(bannerImage)}
                        />
                    </div>
                    <div
                        className={cn(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.pxSmall,
                            dibsCss.myMedium,
                            dibsCss.itemsCenter
                        )}
                    >
                        <span className={cn(dibsCss.sassyFontHeaderSizeLarge, dibsCss.mbSmall)}>
                            {title}
                        </span>
                        {cta && (
                            <div>
                                <Button size="medium" type="transparent" fullWidth={false}>
                                    {cta}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </Link>
        </>
    ) : (
        <>
            <VisibilityTracker
                elementRef={featuredBannerRespRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackPromoImpressions([promoTrackingObject]);
                    }
                }}
            />
            <Link
                href={ctaLink}
                className={styles.link}
                onClick={() => trackPromoClick(promoTrackingObject)}
            >
                <div
                    ref={featuredBannerRespRef}
                    className={cn(
                        dibsCss.flex,
                        dibsCss.wFull,
                        dibsCss.hFull,
                        dibsCss.relative,
                        dibsCss.myLarger,
                        dibsCss.flexRowReverse,
                        styles.consumerBg
                    )}
                >
                    <div className={cn(dibsCss.basis50, dibsCss.h500px, dibsCss.overflowHidden)}>
                        <img
                            alt=""
                            sizes="50vw"
                            src={bannerImage}
                            className={cn(dibsCss.wFull, dibsCss.hFull, dibsCss.objectCover)}
                            srcSet={getSrcsetString(bannerImage, [960, 1280, 1600, 1920])}
                        />
                    </div>
                    <div
                        className={cn(
                            dibsCss.flex,
                            dibsCss.flexCol,
                            dibsCss.basis50,
                            dibsCss.pxLarger,
                            dibsCss.gapXsmall,
                            dibsCss.justifyCenter
                        )}
                    >
                        <span
                            className={cn(
                                dibsCss.sassyFontHeaderSizeXLargeTypeNormal,
                                styles.respTitle
                            )}
                        >
                            {title}
                        </span>
                        {subText && (
                            <span
                                className={cn(
                                    dibsCss.sassyFontHeaderLargeNormal,
                                    dibsCss.mbXsmall,
                                    styles.respDescription
                                )}
                            >
                                {subText}
                            </span>
                        )}
                        {cta && (
                            <div>
                                <Button size="large" type="transparent" fullWidth={false}>
                                    {cta}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </Link>
        </>
    );
};
